/**
 * EquipmentUnmergePanel - Drawer for unmerging BRS or Charterhouse from a product
 */
import { useState, useEffect } from 'react';
import {
  Drawer,
  Stack,
  Title,
  Text,
  Button,
  Group,
  Alert,
  Loader,
  Center,
  Divider,
  ScrollArea,
} from '@mantine/core';
import { IconAlertCircle, IconUnlink } from '@tabler/icons-react';
import { equipmentCatalogApi } from '@/api/equipmentCatalog';
import type { EquipmentProductWithSources } from '@/types/equipmentCatalog';
import { EquipmentFieldComparisonRow } from './EquipmentFieldComparisonRow';

const MERGEABLE_FIELDS = [
  { name: 'name', label: 'Product Name' },
  { name: 'image_url', label: 'Image URL' },
] as const;

type MergeableFieldName = (typeof MERGEABLE_FIELDS)[number]['name'];

interface EquipmentUnmergePanelProps {
  opened: boolean;
  onClose: () => void;
  productId: string;
  sourceToUnmerge: 'brs' | 'charterhouse';
  onSuccess: (newProductId: string) => void;
}

export function EquipmentUnmergePanel({
  opened,
  onClose,
  productId,
  sourceToUnmerge,
  onSuccess,
}: EquipmentUnmergePanelProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [product, setProduct] = useState<EquipmentProductWithSources | null>(null);
  const [fieldSelections, setFieldSelections] = useState<Record<string, string>>({});

  useEffect(() => {
    if (opened && productId) {
      setLoading(true);
      setError(null);

      equipmentCatalogApi
        .getProductWithSources(productId)
        .then((data) => {
          setProduct(data);
          const initialSelections: Record<string, string> = {};
          for (const field of MERGEABLE_FIELDS) {
            initialSelections[field.name] = sourceToUnmerge;
          }
          setFieldSelections(initialSelections);
        })
        .catch((err) => {
          setError(err.message || 'Failed to load product data');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [opened, productId, sourceToUnmerge]);

  useEffect(() => {
    if (!opened) {
      setProduct(null);
      setFieldSelections({});
      setError(null);
    }
  }, [opened]);

  const handleFieldSelect = (fieldName: string, source: string) => {
    setFieldSelections((prev) => ({ ...prev, [fieldName]: source }));
  };

  const handleConfirmUnmerge = async () => {
    if (!productId || !product) return;

    setSubmitting(true);
    setError(null);

    try {
      const response = await equipmentCatalogApi.unmergeProduct({
        product_id: productId,
        source_to_unmerge: sourceToUnmerge,
        field_selections: fieldSelections,
      });
      onSuccess(response.new_product_id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unmerge source');
    } finally {
      setSubmitting(false);
    }
  };

  const getFieldValues = (fieldName: MergeableFieldName) => {
    if (!product) return [];
    return [
      { source: 'current', value: product[fieldName], label: 'Current' },
      { source: sourceToUnmerge, value: product[fieldName], label: sourceToUnmerge.toUpperCase() },
    ];
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Center h={200} data-testid="loading-state">
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Loading product data...</Text>
          </Stack>
        </Center>
      );
    }

    if (error) {
      return (
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" data-testid="error-state">
          {error}
        </Alert>
      );
    }

    if (!product) return null;

    return (
      <Stack gap="md">
        <Alert color="blue" variant="light">
          <Text size="sm">
            Unmerging <strong>{sourceToUnmerge.toUpperCase()}</strong> source from this product.
            The unmerged source will become its own separate product.
          </Text>
        </Alert>

        <Divider label="Field Values" labelPosition="center" />

        <ScrollArea.Autosize mah="calc(100vh - 350px)" offsetScrollbars>
          <Stack gap="xs">
            {MERGEABLE_FIELDS.map((field) => (
              <EquipmentFieldComparisonRow
                key={field.name}
                fieldName={field.name}
                fieldLabel={field.label}
                values={getFieldValues(field.name)}
                selectedSource={fieldSelections[field.name] || sourceToUnmerge}
                onSelect={(source) => handleFieldSelect(field.name, source)}
              />
            ))}
          </Stack>
        </ScrollArea.Autosize>

        <Divider />

        <Group justify="flex-end" gap="md">
          <Button variant="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            color="red"
            leftSection={<IconUnlink size={16} />}
            onClick={handleConfirmUnmerge}
            loading={submitting}
            data-testid="confirm-unmerge-button"
          >
            Confirm Unmerge
          </Button>
        </Group>
      </Stack>
    );
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title={
        <Group gap="sm">
          <IconUnlink size={20} />
          <Title order={4}>Unmerge Source</Title>
        </Group>
      }
      padding="md"
    >
      {renderContent()}
    </Drawer>
  );
}
